import { onSnapshot } from 'firebase/firestore';
import {
  clearOnboardingAnswers,
  getOnboardingAnswers,
} from 'shared/services/local-storage';
import { NewGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { OnboardingGoal } from 'shared/types/onboarding-answers';
import { MinimalNewTask } from 'shared/types/task';
import { FavoriteReference, FavoriteType } from 'shared/types/user-settings';

import { createGoals } from './create-goals';
import { createTasks } from './create-tasks';
import { getLifeAreas } from './get-life-areas';
import { updateLifeAreas } from './update-life-areas';
import { updateUserSettings } from './update-user-settings';

const getLifeAreasDocs = async () => {
  const query = await getLifeAreas();
  return new Promise<LifeArea[]>((resolve, reject) => {
    const unsubscribe = onSnapshot(query, (docs) => {
      if (!docs.empty) {
        clearTimeout(timeout);
        unsubscribe();
        resolve(docs.docs.map((doc) => doc.data() as LifeArea));
      }
    });

    // if we have no life areas in 5 seconds, we reject
    const timeout = setTimeout(() => {
      unsubscribe();
      reject();
    }, 15000);
  });
};

const createAndFavoritizeGoals = async (
  goals: OnboardingGoal[] | undefined,
  lifeAreaIdMap: Map<ID, ID>,
) => {
  if (!goals?.length) {
    return [];
  }

  const goalsToCreate: NewGoal[] = goals.map((goal) => {
    // Create a new goal object without the id (as it's a temporary id)
    const { id: _id, ...goalWithoutId } = goal;

    // Replace lifeAreaId with the actual lifeAreaId
    const actualLifeAreaId = lifeAreaIdMap.get(goal.lifeAreaId as ID);

    return {
      ...goalWithoutId,
      lifeAreaId: actualLifeAreaId || null,
    };
  });

  // Create goals and get the created goals with their new IDs
  const [createdGoals] = await Promise.all([
    createGoals(goalsToCreate),
    new Promise((resolve) => setTimeout(() => resolve(true), 100)),
  ]);

  const favorites: FavoriteReference[] = createdGoals.map((goal) => ({
    type: FavoriteType.Goal,
    id: goal.id,
  }));

  await updateUserSettings({ favorites });

  return createdGoals;
};

export const processOnboardingAnswers = async () => {
  // Get onboarding answers from localStorage
  const onboardingAnswers = getOnboardingAnswers();

  // If no onboarding answers, return early
  if (!onboardingAnswers) {
    return;
  }

  // Get user's life areas
  const lifeAreas = await getLifeAreasDocs();

  // Archive life areas based on templateId
  if (onboardingAnswers.archivedLifeAreas?.length) {
    const lifeAreasToArchive = lifeAreas.filter((lifeArea) =>
      onboardingAnswers.archivedLifeAreas?.includes(lifeArea.templateId as ID),
    );

    if (lifeAreasToArchive.length) {
      await updateLifeAreas(
        lifeAreasToArchive.map((lifeArea) => ({
          id: lifeArea.id,
          archivedAt: new Date(),
        })),
      );
    }
  }

  // create a map of lifeAreaTemplateId to actual lifeAreaId
  const lifeAreaIdMap = new Map<ID, ID>();
  lifeAreas.forEach((lifeArea) => {
    if (lifeArea.templateId) {
      lifeAreaIdMap.set(lifeArea.templateId as ID, lifeArea.id);
    }
  });

  // Create goals with updated lifeAreaId references
  const createdGoals = await createAndFavoritizeGoals(
    onboardingAnswers.goals,
    lifeAreaIdMap,
  );

  // Create a map of temporary goalId to actual goalId
  const goalIdMap = new Map<ID, ID>();
  onboardingAnswers.goals?.forEach((goal, index) => {
    if (createdGoals[index]) {
      goalIdMap.set(goal.id, createdGoals[index].id);
    }
  });

  const allTasksToCreate: MinimalNewTask[] = [];

  // Create habits with proper ID references
  onboardingAnswers.habits?.forEach((habit) => {
    allTasksToCreate.push({
      ...habit,
      goalId: habit.goalId ? goalIdMap.get(habit.goalId) ?? null : null,
      lifeAreaId: habit.lifeAreaId
        ? lifeAreaIdMap.get(habit.lifeAreaId) ?? null
        : null,
    });
  });

  // Create tasks with proper ID references
  onboardingAnswers.tasks?.forEach((task) => {
    allTasksToCreate.push({
      ...task,
      goalId: task.goalId ? goalIdMap.get(task.goalId) ?? null : null,
      lifeAreaId: task.lifeAreaId
        ? lifeAreaIdMap.get(task.lifeAreaId) ?? null
        : null,
    });
  });

  await createTasks(allTasksToCreate);

  // Clear onboarding answers from localStorage
  clearOnboardingAnswers();
};
